import { create } from "zustand";
import {
  isChunkWithinViewableRange,
  isPageInViewableRange,
} from "../services/drmPolicyService";
import { loadEpubDocument } from "../services/epubService";
import {
  getReadingDirectionFromMetadata,
  READING_DIRECTION_LTR,
  READING_DIRECTION_RTL,
} from "../utils/readingDirection";
import {
  isValidEpubFont,
  isValidReaderTheme,
  normalizeEpubLineHeight,
  normalizeReaderTheme,
  EPUB_LINE_HEIGHT_DEFAULT,
} from "../utils/epubThemes";
import {
  fetchArrayBufferFromUrl,
  getPresignedUrl,
  sendReaderLog,
} from "../services/igpService";
import {
  showFeatureNotAllowedNotification,
  showSessionExpiredNotification,
} from "../services/notificationService";
import { loadPdfDocument } from "../services/pdfService";

const getSessionAuthTokens = () => {
  const savedSession = sessionStorage.getItem("igp_session");
  if (!savedSession) return { catToken: null, rstToken: null };

  try {
    const session = JSON.parse(savedSession);
    return {
      catToken: session.catToken || null,
      rstToken: session.rstToken || null,
    };
  } catch (error) {
    return { catToken: null, rstToken: null };
  }
};

// Define types for our store
const useReaderStore = create((set, get) => ({
  // Document state
  document: null,
  documentType: null, // 'pdf' or 'epub'
  documentName: "",
  isLoading: false,
  error: null,

  // Navigation state
  currentPage: 1,
  currentPageEnd: null,
  totalPages: 0,
  scale: 1.0,
  pdfRotation: 0,

  // Table of contents
  toc: [],
  isTocSidebarOpen: false,
  isBookmarksSidebarOpen: false,
  isNotesSidebarOpen: false,
  isSearchSidebarOpen: false,
  isPagesSidebarOpen: false,
  isReadingAssistantSidebarOpen: false,
  isPublicationDetailsSidebarOpen: false,
  // Prefill Ask AI input when opened from selection menu
  askAiPrefillText: null,
  readingAssistantActiveTab: null,

  // EPUB rendition reference for navigation
  epubRendition: null,

  // Annotation tools (only one can be active at a time)
  highlightEnabled: false,
  highlightColor: "#FFF59D", // Yellow – used for new highlights
  drawingEnabled: false,
  notesEnabled: false,
  underlineEnabled: false,
  textboxEnabled: false,

  // Notes
  noteListVersion: 0,

  // API Annotations - Nested structure
  annotations: {
    bookmarks: { data: [], isLoading: false, error: null },
    highlights: { data: [], isLoading: false, error: null },
    underlines: { data: [], isLoading: false, error: null },
    notes: { data: [], isLoading: false, error: null },
    comments: { data: [], isLoading: false, error: null },
    freehand_drawing: { data: [], isLoading: false, error: null },
    textboxes: { data: [], isLoading: false, error: null },
  },

  pageViewMode:
    typeof window !== "undefined" &&
    sessionStorage.getItem("igp-reader-page-view-mode") === "single"
      ? "single"
      : "double",

  // EPUB page-progression direction ("ltr" | "rtl"), from metadata.read_direction
  readingDirection: READING_DIRECTION_LTR,

  // Theme: light | dark | mint | sepia (content + chrome)
  theme: (() => {
    if (typeof window === "undefined") return "dark";
    return normalizeReaderTheme(localStorage.getItem("igp-reader-theme"));
  })(),

  // EPUB typography — session-only (not persisted); PDF ignores these
  epubFontFamily: "original",
  epubLineHeight: EPUB_LINE_HEIGHT_DEFAULT,

  // Reader View Mode (fit-to-page, fit-to-width)
  readerViewMode: "fit-to-width",

  // Search
  searchQuery: "",
  searchResults: [],
  isSearching: false,

  // Chunked loading state
  isChunked: false,
  metadata: null,
  loadedChunks: {}, // { chunkIndex: pdfDocument }
  chunkLoadPromises: {}, // { chunkIndex: Promise }
  documentId: "",
  initialChapterNumber: 1,
  epubCurrentChapterNumber: 1,

  // DRM Policy
  drmPolicy: null,

  // Initial view from API (page_display_style + start_page)
  initialViewStartPage: null,

  // Actions
  setDocument: (document, documentType, documentName, options = {}) =>
    set({
      document,
      documentType,
      documentName,
      isLoading: false,
      error: null,
      isChunked: options.isChunked || false,
      metadata: options.metadata || null,
      documentId: options.documentId || "",
      loadedChunks: options.loadedChunks || {},
      chunkLoadPromises: {},
      initialChapterNumber: options.initialChapterNumber || 1,
      initialViewStartPage: null,
      epubCurrentChapterNumber: options.initialChapterNumber || 1,
      pdfRotation: 0,
      readingDirection:
        documentType === "epub"
          ? getReadingDirectionFromMetadata(options.metadata)
          : READING_DIRECTION_LTR,
    }),

  setChunkLoaded: (chunkIndex, chunkDocument) =>
    set((state) => ({
      loadedChunks: {
        ...state.loadedChunks,
        [chunkIndex]: chunkDocument,
      },
    })),

  loadChunk: async (chunkIndex) => {
    const state = get();
    // 1. Check if already loaded
    if (state.loadedChunks[chunkIndex]) return state.loadedChunks[chunkIndex];
    // 2. Check if already loading
    if (state.chunkLoadPromises[chunkIndex])
      return state.chunkLoadPromises[chunkIndex];

    const { metadata, documentId, setChunkLoaded, drmPolicy } = state;
    if (!metadata) return null;

    // 3. Check if chunk index is within bounds and contains pages within viewable range
    const chunkSize = 5;
    const start = chunkIndex * chunkSize + 1;
    if (start > metadata.total_pages) {
      return null;
    }

    if (
      !isChunkWithinViewableRange(drmPolicy, chunkIndex, metadata.total_pages)
    ) {
      return null;
    }

    // Get tokens from sessionStorage (single source of truth)
    const { catToken, rstToken } = getSessionAuthTokens();
    if (!catToken) {
      throw new Error("No CAT token available for chunk loading");
    }

    // 4. Create promise
    const loadPromise = (async () => {
      try {
        const chunkSize = 5; // Fixed chunk size
        const start = chunkIndex * chunkSize + 1;
        const end = Math.min(
          (chunkIndex + 1) * chunkSize,
          metadata.total_pages,
        );
        const chunkName = `pages/${start}-${end}.pdf`;
        const response = await getPresignedUrl(catToken, documentId, chunkName);
        const buffer = await fetchArrayBufferFromUrl(response.presigned_url);
        const chunkDoc = await loadPdfDocument(buffer);
        setChunkLoaded(chunkIndex, chunkDoc);
        // Send analytics log for chunk load (fire and forget)
        sendReaderLog(
          documentId,
          "reading",
          {
            event: "load-chunk",
            value: `${start}-${end} chunk loaded`,
          },
          rstToken,
        );
        // Remove from loading promises
        set((s) => {
          const newPromises = { ...s.chunkLoadPromises };
          delete newPromises[chunkIndex];
          return { chunkLoadPromises: newPromises };
        });
        return chunkDoc;
      } catch (err) {
        console.error(`Failed to load chunk ${chunkIndex}`, err);
        // Check if 401 error (expired token)
        if (
          err.response?.status === 401 ||
          err.message?.includes("CAT token")
        ) {
          showSessionExpiredNotification();
        }
        // Remove from loading promises
        set((s) => {
          const newPromises = { ...s.chunkLoadPromises };
          delete newPromises[chunkIndex];
          return { chunkLoadPromises: newPromises };
        });
        throw err;
      }
    })();

    // 4. Save promise
    set((s) => ({
      chunkLoadPromises: { ...s.chunkLoadPromises, [chunkIndex]: loadPromise },
    }));
    return loadPromise;
  },

  loadEpubChapter: async (chapterNumber) => {
    const state = get();
    // 1. Check if already loaded
    if (state.loadedChunks[chapterNumber])
      return state.loadedChunks[chapterNumber];
    // 2. Check if already loading
    if (state.chunkLoadPromises[chapterNumber])
      return state.chunkLoadPromises[chapterNumber];

    const { documentId, setChunkLoaded } = state;
    if (!documentId) return null;

    // Get tokens from sessionStorage (single source of truth)
    const { catToken, rstToken } = getSessionAuthTokens();
    if (!catToken) {
      throw new Error("No CAT token available for chunk loading");
    }

    // 4. Create promise
    const loadPromise = (async () => {
      try {
        const chunkName = `pages/chapter_${chapterNumber}.epub`;
        const response = await getPresignedUrl(catToken, documentId, chunkName);
        const buffer = await fetchArrayBufferFromUrl(response.presigned_url);
        const chapterDoc = await loadEpubDocument(buffer);
        setChunkLoaded(chapterNumber, chapterDoc);
        // Send analytics log for epub chapter load (fire and forget)
        sendReaderLog(
          documentId,
          "reading",
          {
            event: "load-chunk",
            value: `chapter_${chapterNumber} loaded`,
          },
          rstToken,
        );
        // Remove from loading promises
        set((s) => {
          const newPromises = { ...s.chunkLoadPromises };
          delete newPromises[chapterNumber];
          return { chunkLoadPromises: newPromises };
        });
        return chapterDoc;
      } catch (err) {
        console.error(`Failed to load EPUB chapter ${chapterNumber}:`, err);
        // Check if 401 error (expired token)
        if (err.response?.status === 401 || err.message?.includes("401")) {
          showSessionExpiredNotification();
        }
        // Remove from loading promises
        set((s) => {
          const newPromises = { ...s.chunkLoadPromises };
          delete newPromises[chapterNumber];
          return { chunkLoadPromises: newPromises };
        });
        throw err;
      }
    })();

    // 4. Save promise
    set((s) => ({
      chunkLoadPromises: {
        ...s.chunkLoadPromises,
        [chapterNumber]: loadPromise,
      },
    }));
    return loadPromise;
  },

  clearDocument: () =>
    set({
      document: null,
      documentType: null,
      documentName: "",
      currentPage: 1,
      currentPageEnd: null,
      totalPages: 0,
      toc: [],
      epubRendition: null,
      isChunked: false,
      metadata: null,
      loadedChunks: {},
      chunkLoadPromises: {},
      documentId: "",
      readingDirection: READING_DIRECTION_LTR,
      annotations: {
        bookmarks: { data: [], isLoading: false, error: null },

        highlights: { data: [], isLoading: false, error: null },
        underlines: { data: [], isLoading: false, error: null },
        notes: { data: [], isLoading: false, error: null },
        comments: { data: [], isLoading: false, error: null },
        freehand_drawing: { data: [], isLoading: false, error: null },
        textboxes: { data: [], isLoading: false, error: null },
      },
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => {
    let finalError = error;
    const errorStr = typeof error === "string" ? error : error?.message || "";
    if (errorStr && errorStr.includes("withResolvers")) {
      finalError =
        "Your browser or device is not supported. Please update to a newer browser version to view this document.";
    }
    set({ error: finalError, isLoading: false });
  },

  setCurrentPage: (currentPage) =>
    set((state) => {
      // Check if page is within DRM policy viewable range
      if (!isPageInViewableRange(state.drmPolicy, currentPage)) {
        showFeatureNotAllowedNotification("page_out_of_range");
        return state; // Don't change page if outside allowed range
      }
      return { currentPage };
    }),
  setCurrentPageEnd: (currentPageEnd) => set({ currentPageEnd }),
  // Atomic setter: updates both currentPage and currentPageEnd in a single
  // Zustand set() call so React never renders an intermediate state where
  // one value is fresh and the other is stale (avoids "2-1" flash).
  setCurrentPageWithEnd: (currentPage, currentPageEnd) =>
    set((state) => {
      if (!isPageInViewableRange(state.drmPolicy, currentPage)) {
        showFeatureNotAllowedNotification("page_out_of_range");
        return state;
      }
      return { currentPage, currentPageEnd };
    }),
  setTotalPages: (totalPages) => set({ totalPages }),

  setEpubCurrentChapterNumber: (epubCurrentChapterNumber) =>
    set({ epubCurrentChapterNumber }),

  setScale: (scale) => set({ scale }),
  setPdfRotation: (pdfRotationOrUpdater) =>
    set((state) => {
      const next =
        typeof pdfRotationOrUpdater === "function"
          ? pdfRotationOrUpdater(state.pdfRotation)
          : pdfRotationOrUpdater;
      const normalized = ((Number(next) % 360) + 360) % 360;
      return {
        pdfRotation: Number.isFinite(normalized) ? normalized : 0,
      };
    }),

  setToc: (toc) => set({ toc }),

  toggleTocSidebar: () =>
    set((state) => {
      if (state.isTocSidebarOpen) {
        // Close TOC
        return { isTocSidebarOpen: false };
      } else {
        // Open TOC, close other sidebars
        return {
          isTocSidebarOpen: true,
          isBookmarksSidebarOpen: false,
          isNotesSidebarOpen: false,
          isSearchSidebarOpen: false,
          isPagesSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      }
    }),

  toggleBookmarksSidebar: () =>
    set((state) => {
      if (state.isBookmarksSidebarOpen) {
        // Close Bookmarks
        return { isBookmarksSidebarOpen: false };
      } else {
        // Open Bookmarks, close other sidebars
        return {
          isBookmarksSidebarOpen: true,
          isTocSidebarOpen: false,
          isNotesSidebarOpen: false,
          isSearchSidebarOpen: false,
          isPagesSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      }
    }),

  toggleNotesSidebar: () =>
    set((state) => {
      if (state.isNotesSidebarOpen) {
        // Close Notes
        return { isNotesSidebarOpen: false };
      } else {
        // Open Notes, close other sidebars
        return {
          isNotesSidebarOpen: true,
          isTocSidebarOpen: false,
          isBookmarksSidebarOpen: false,
          isSearchSidebarOpen: false,
          isPagesSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      }
    }),

  toggleSearchSidebar: () =>
    set((state) => {
      if (state.isSearchSidebarOpen) {
        // Close Search and clear global search state so temporary highlights are removed
        return {
          isSearchSidebarOpen: false,
          searchQuery: "",
          searchResults: [],
          isSearching: false,
        };
      } else {
        // Open Search, close other sidebars
        return {
          isSearchSidebarOpen: true,
          isTocSidebarOpen: false,
          isBookmarksSidebarOpen: false,
          isNotesSidebarOpen: false,
          isPagesSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      }
    }),

  setSearchSidebarOpen: (isOpen) =>
    set((state) => {
      if (isOpen) {
        // Open Search, close other sidebars
        return {
          isSearchSidebarOpen: true,
          isTocSidebarOpen: false,
          isBookmarksSidebarOpen: false,
          isNotesSidebarOpen: false,
          isPagesSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      } else {
        // Close Search and clear global search state so temporary highlights are removed
        return {
          isSearchSidebarOpen: false,
          searchQuery: "",
          searchResults: [],
          isSearching: false,
        };
      }
    }),

  // Close the search sidebar panel visually WITHOUT clearing search state.
  // Used on mobile when the user taps a result — the sidebar hides but
  // searchResults/query are preserved so highlights remain visible.
  hideSearchSidebar: () => set({ isSearchSidebarOpen: false }),

  togglePagesSidebar: () =>
    set((state) => {
      if (state.isPagesSidebarOpen) {
        // Close Pages
        return { isPagesSidebarOpen: false };
      } else {
        // Open Pages, close other sidebars
        return {
          isPagesSidebarOpen: true,
          isTocSidebarOpen: false,
          isBookmarksSidebarOpen: false,
          isNotesSidebarOpen: false,
          isSearchSidebarOpen: false,
          isReadingAssistantSidebarOpen: false,
          isPublicationDetailsSidebarOpen: false,
        };
      }
    }),

  toggleReadingAssistantSidebar: () =>
    set((state) => {
      if (state.isReadingAssistantSidebarOpen) {
        return { isReadingAssistantSidebarOpen: false };
      }
      return {
        isReadingAssistantSidebarOpen: true,
        isTocSidebarOpen: false,
        isBookmarksSidebarOpen: false,
        isNotesSidebarOpen: false,
        isSearchSidebarOpen: false,
        isPagesSidebarOpen: false,
        isPublicationDetailsSidebarOpen: false,
      };
    }),

  openReadingAssistantAskAi: (prefillText) =>
    set({
      isReadingAssistantSidebarOpen: true,
      askAiPrefillText:
        typeof prefillText === "string" ? prefillText.trim() : "",
      readingAssistantActiveTab: "askAi",
      isTocSidebarOpen: false,
      isBookmarksSidebarOpen: false,
      isNotesSidebarOpen: false,
      isSearchSidebarOpen: false,
      isPagesSidebarOpen: false,
      isPublicationDetailsSidebarOpen: false,
    }),

  setAskAiPrefillText: (askAiPrefillText) => set({ askAiPrefillText }),
  clearAskAiPrefillText: () => set({ askAiPrefillText: null }),
  setReadingAssistantActiveTab: (readingAssistantActiveTab) =>
    set({ readingAssistantActiveTab }),
  clearReadingAssistantActiveTab: () =>
    set({ readingAssistantActiveTab: null }),

  togglePublicationDetailsSidebar: () =>
    set((state) => {
      if (state.isPublicationDetailsSidebarOpen) {
        return { isPublicationDetailsSidebarOpen: false };
      }
      return {
        isPublicationDetailsSidebarOpen: true,
        isTocSidebarOpen: false,
        isBookmarksSidebarOpen: false,
        isNotesSidebarOpen: false,
        isSearchSidebarOpen: false,
        isPagesSidebarOpen: false,
        isReadingAssistantSidebarOpen: false,
      };
    }),

  setEpubRendition: (epubRendition) => set({ epubRendition }),

  // Annotation tool actions (mutually exclusive)
  setHighlightEnabled: (highlightEnabled) =>
    set((state) => {
      if (highlightEnabled) {
        return {
          highlightEnabled: true,
          drawingEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      } else {
        return { highlightEnabled: false };
      }
    }),
  toggleHighlightEnabled: () =>
    set((state) => {
      if (state.highlightEnabled) {
        return { highlightEnabled: false };
      } else {
        return {
          highlightEnabled: true,
          drawingEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      }
    }),

  setHighlightColor: (highlightColor) => set({ highlightColor }),

  setDrawingEnabled: (drawingEnabled) =>
    set((state) => {
      if (drawingEnabled) {
        return {
          drawingEnabled: true,
          highlightEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      } else {
        return { drawingEnabled: false };
      }
    }),
  toggleDrawingEnabled: () =>
    set((state) => {
      if (state.drawingEnabled) {
        return { drawingEnabled: false };
      } else {
        return {
          drawingEnabled: true,
          highlightEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      }
    }),

  // Note actions (mutually exclusive with other annotation tools)
  setNotesEnabled: (notesEnabled) =>
    set((state) => {
      if (notesEnabled) {
        return {
          notesEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      } else {
        return { notesEnabled: false };
      }
    }),
  toggleNotesEnabled: () =>
    set((state) => {
      if (state.notesEnabled) {
        return { notesEnabled: false };
      } else {
        return {
          notesEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          underlineEnabled: false,
          textboxEnabled: false,
        };
      }
    }),

  // Underline actions (mutually exclusive with other annotation tools)
  setUnderlineEnabled: (underlineEnabled) =>
    set((state) => {
      if (underlineEnabled) {
        return {
          underlineEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          notesEnabled: false,
          textboxEnabled: false,
        };
      } else {
        return { underlineEnabled: false };
      }
    }),
  toggleUnderlineEnabled: () =>
    set((state) => {
      if (state.underlineEnabled) {
        return { underlineEnabled: false };
      } else {
        return {
          underlineEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          notesEnabled: false,
          textboxEnabled: false,
        };
      }
    }),

  // Textbox actions (mutually exclusive with other annotation tools)
  setTextboxEnabled: (textboxEnabled) =>
    set((state) => {
      if (textboxEnabled) {
        return {
          textboxEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
        };
      } else {
        return { textboxEnabled: false };
      }
    }),
  toggleTextboxEnabled: () =>
    set((state) => {
      if (state.textboxEnabled) {
        return { textboxEnabled: false };
      } else {
        return {
          textboxEnabled: true,
          highlightEnabled: false,
          drawingEnabled: false,
          notesEnabled: false,
          underlineEnabled: false,
        };
      }
    }),

  refreshNotes: () =>
    set((state) => ({ noteListVersion: state.noteListVersion + 1 })),

  setPageViewMode: (mode) => {
    const normalized = mode === "double" ? "double" : "single";
    if (typeof window !== "undefined") {
      sessionStorage.setItem("igp-reader-page-view-mode", normalized);
    }
    set({ pageViewMode: normalized });
  },

  setReadingDirection: (direction) => {
    const normalized =
      direction === READING_DIRECTION_RTL
        ? READING_DIRECTION_RTL
        : READING_DIRECTION_LTR;
    set({ readingDirection: normalized });
  },

  setTheme: (theme) => {
    const next = isValidReaderTheme(theme) ? theme : "dark";
    if (typeof window !== "undefined") {
      localStorage.setItem("igp-reader-theme", next);
    }
    set({ theme: next });
  },

  setEpubFontFamily: (fontId) => {
    set({ epubFontFamily: isValidEpubFont(fontId) ? fontId : "original" });
  },

  setEpubLineHeight: (lineHeight) => {
    set({ epubLineHeight: normalizeEpubLineHeight(lineHeight) });
  },

  setReaderViewMode: (readerViewMode) => set({ readerViewMode }),

  // Generic Annotation Actions - Works for all types (bookmarks, highlights, notes, comments)
  setAnnotationData: (type, data) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: { ...state.annotations[type], data },
      },
    })),
  setAnnotationLoading: (type, isLoading) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: { ...state.annotations[type], isLoading },
      },
    })),
  setAnnotationError: (type, error) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: { ...state.annotations[type], error },
      },
    })),
  addAnnotationToStore: (type, annotation) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: {
          ...state.annotations[type],
          data: [...state.annotations[type].data, annotation],
        },
      },
    })),
  removeAnnotationFromStore: (type, id) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: {
          ...state.annotations[type],
          data: state.annotations[type].data.filter((item) => item.id !== id),
        },
      },
    })),
  updateAnnotationInStore: (type, id, updates) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: {
          ...state.annotations[type],
          data: state.annotations[type].data.map((item) =>
            item.id === id ? { ...item, ...updates } : item,
          ),
        },
      },
    })),
  clearAnnotations: (type) =>
    set((state) => ({
      annotations: {
        ...state.annotations,
        [type]: { data: [], isLoading: false, error: null },
      },
    })),

  // Search actions
  setSearchQuery: (searchQuery) =>
    set((state) => {
      const isQueryEmpty = !searchQuery || !searchQuery.trim();
      return {
        searchQuery,
        ...(isQueryEmpty ? { searchResults: [], isSearching: false } : {}),
      };
    }),
  clearSearchQuery: () =>
    set({ searchQuery: "", searchResults: [], isSearching: false }),
  setSearchResults: (searchResults) => set({ searchResults }),
  setIsSearching: (isSearching) => set({ isSearching }),

  // DRM Policy action
  setDrmPolicy: (drmPolicy) => set({ drmPolicy }),

  // Initial view actions (from fetchAccessData initial_view). Set currentPage immediately so DRM start page is applied even before viewer effects run.
  setInitialView: (pageDisplayStyle, startPage) =>
    set((state) => {
      const savedMode =
        typeof window !== "undefined"
          ? sessionStorage.getItem("igp-reader-page-view-mode")
          : null;
      const pageViewMode =
        savedMode === "double" || savedMode === "single"
          ? savedMode
          : pageDisplayStyle === "double"
            ? "double"
            : "single";
      const effectiveStart =
        startPage != null && startPage > 0 ? startPage : null;
      return {
        pageViewMode,
        initialViewStartPage: effectiveStart,
        ...(effectiveStart != null ? { currentPage: effectiveStart } : {}),
      };
    }),
  clearInitialViewStartPage: () => set({ initialViewStartPage: null }),
}));

export default useReaderStore;
